import {uniqBy} from 'lodash'

import {generateContract} from 'models/Contract'
import Calculator from 'models/Calculator'
import {types} from './actions'
import {getCart} from '../cart/reducer'
import {strAmountIsZero} from '../../utils/contracts'
import {getContracts} from '../contracts/reducer'
import {getFamily} from '../families/reducer'
import {selectProfile} from 'store/profile/reducer'
import {calculateEffectiveUserBag, getActiveMonths} from './calculateEffectiveUserBag'

const initialState = []

export default (appState = initialState, { type, payload }) => {
    switch (type) {
        case types.SIMULATOR_ADD:
            return [...appState, payload]

        case types.SIMULATOR_UPDATE:
            if (appState.map(item => item.id).indexOf(payload.id) < 0) {
                return [...appState, payload]
            } else {
                return appState.map(item => {
                        return item.id === payload.id
                            ? payload
                            : item
                    }
                )
            }

        case types.SIMULATOR_UPDATE_ID:
            return appState.map(item =>
                item.id === payload.oldId
                    ? generateContract({
                        ...item,
                        id: payload.newId,
                    })
                    : item
            )

        case types.SIMULATOR_REMOVE:
            return appState.filter(item => item.id !== payload)

        default:
            return appState
    }
}

export const getContractItems = state => {
    const cart = getCart(state)
    const contracts = getContracts(state)

    return [...cart, ...contracts]
}

export const getSimulatorItems = state => {
    const cart = getContractItems(state)
    const virtualItems = state.simulator
    const items = uniqBy([...virtualItems, ...cart], 'benefitId')

    // Sort items to ensure consistent processing order
    // This is critical for userBag calculation: items are processed in order,
    // and the first items get priority access to the monthly budget.
    // Priority: numeric IDs first (contracts), then string IDs (virtual items)
    items.sort((a, b) => {
        const aIdIsNumber = typeof a.id === 'number'
        const bIdIsNumber = typeof b.id === 'number'

        // Numeric IDs (contracts) have priority over string IDs (virtual items)
        if (aIdIsNumber && !bIdIsNumber) return -1
        if (!aIdIsNumber && bIdIsNumber) return 1

        // Both numeric: sort by value
        if (aIdIsNumber && bIdIsNumber) {
            return a.id - b.id
        }

        // Both string: sort alphabetically
        if (a.id < b.id) return -1
        if (a.id > b.id) return 1

        // Same ID: sort by benefitId
        return (a.benefitId || 0) - (b.benefitId || 0)
    })

    return items
}

export const getTicketsPrice = (contractDetails) => {
    if (contractDetails && contractDetails.ticketPrice && contractDetails.ticketCount) {
        return contractDetails.ticketPrice * contractDetails.ticketCount
    }
    return 0
}

// returns the total price
export const getSimulation = state => {

    let {
        flexibleBag,
        companyBag,
        topBag,
        userBag,
        fixedSalary,
        privateUse,
        renting,
        userBagProrate,
        userBagRestricted,
        monthsRemaining,
        payPerProduct,
        products,
    } = selectProfile(state)
    const items = getSimulatorItems(state)

    userBag = userBag || 0
    let advantagePrice = 0
    let userBagSpent = 0
    let otherPrice = 0
    let contributionById = {}
    let pricesById = {}
    let userBagById = {}
    const activeMonths = items.length > 0
        ? getActiveMonths(items[0])
        : monthsRemaining

    const effectiveUserBag = calculateEffectiveUserBag({ userBag, userBagProrate, userBagRestricted })

    // Global spending trackers across all products
    let globalMonthlySpent = {} // tracks spending per month (for restricted mode)
    let globalTotalSpent = 0 // tracks total spending (for non-restricted mode)

    // The total budget is simply the userBag (e.g., 1500)
    const totalBudget = userBag || 0

    items.forEach(item => {
        if (item.status === 'cancelled') return

        let itemUserBag = 0
        const benefit = item.benefit || {}
        const productId = benefit.product && benefit.product.id
        const profileProduct = products ? products.find(p => p.id === productId) : null

        const isUserBagAllowed = profileProduct ? profileProduct.isUserBag !== false : true

        if (isUserBagAllowed && userBagProrate !== null && userBagProrate !== undefined) {
            if (userBagRestricted) {
                // Restrictive mode: enforce per-month caps only, no global total limit
                const monthsWithValues = item.items ?
                    item.items
                        .filter(monthItem => monthItem.price > 0)
                        .map(monthItem => ({ month: monthItem.month, price: monthItem.contributedPrice !== undefined ? monthItem.contributedPrice : monthItem.price }))
                    : []

                // Monthly per-user limit (e.g., 125)
                const monthlyLimit = userBagProrate

                let totalUsedDiscount = 0

                if (monthsWithValues.length > 0) {
                    monthsWithValues.sort((a, b) => b.price - a.price)

                    monthsWithValues.forEach(monthItem => {
                        const alreadySpentThisMonth = globalMonthlySpent[monthItem.month] || 0
                        const monthlyBudgetRemaining = Math.max(0, monthlyLimit - alreadySpentThisMonth)

                        const discountForThisMonth = Math.min(monthItem.price, monthlyBudgetRemaining)

                        totalUsedDiscount += discountForThisMonth
                        globalMonthlySpent[monthItem.month] = alreadySpentThisMonth + discountForThisMonth
                    })
                }

                itemUserBag = totalUsedDiscount
            } else {
                const monthsWithValues = item.items ?
                    item.items
                        .filter(monthItem => monthItem.price > 0)
                        .map(monthItem => ({ month: monthItem.month, price: monthItem.contributedPrice !== undefined ? monthItem.contributedPrice : monthItem.price }))
                    : []

                const hasMonthsWithValue = monthsWithValues.length > 0

                if (hasMonthsWithValue) {
                    let remainingDiscount = Math.max(0, totalBudget - globalTotalSpent)
                    let totalUsedDiscount = 0

                    monthsWithValues.sort((a, b) => b.price - a.price)

                    monthsWithValues.forEach(monthItem => {
                        const discountForThisMonth = Math.min(monthItem.price, remainingDiscount)
                        remainingDiscount -= discountForThisMonth
                        totalUsedDiscount += discountForThisMonth
                    })

                    itemUserBag = totalUsedDiscount
                    globalTotalSpent += totalUsedDiscount
                } else {
                    itemUserBag = 0
                }
            }
        } else if (isUserBagAllowed) {
            // Legacy mode: cap by totalBudget
            const requestedAmount = parseFloat(item.userBag) || 0
            const globalBudgetRemaining = Math.max(0, totalBudget - globalTotalSpent)
            const maxDiscountableAmount = (item.contributedPrice !== undefined ? item.contributedPrice : item.price)

            if (typeof item.id === 'number' && item.status === 'cart' && item._originalUserBag !== undefined) {
                const originalUserBag = item._originalUserBag
                itemUserBag = Math.min(Math.max(originalUserBag, requestedAmount), globalBudgetRemaining, maxDiscountableAmount)
            } else {
                itemUserBag = Math.min(requestedAmount, globalBudgetRemaining, maxDiscountableAmount)
            }
            
            globalTotalSpent += itemUserBag
        }

        item.userBag = itemUserBag

        userBagSpent += itemUserBag

        if (item.benefit) {
            pricesById[item.benefit['id']] = item.price
            contributionById[item.benefit['id']] = item.contributionCompany
            userBagById[item.benefit['id']] = itemUserBag
        }
        const ticketsPrice = getTicketsPrice(item.details)

        const contributedPriceToUse = (typeof item.id === 'number' && item.status === 'cart' && item._originalContributedPrice !== undefined)
            ? item._originalContributedPrice
            : (item.contributedPrice || ticketsPrice)
        
        const itemFinalPrice = Math.max(0, contributedPriceToUse - itemUserBag)
        
        if (item.benefit.isTaxAdvantage) {
            advantagePrice += itemFinalPrice || ticketsPrice
        } else {
            otherPrice += itemFinalPrice || ticketsPrice
        }
    })

    let available = flexibleBag

    let netPrice = advantagePrice

    if (strAmountIsZero(companyBag)) {
        available -= netPrice
    } else if (!companyBag.endsWith('%')) {
        available +=
            Math.max(0, parseInt(companyBag, 10) - otherPrice) - netPrice
    } else {
        available -= netPrice * (1 - parseInt(companyBag, 10) / 100)
    }

    const availableTop = topBag - otherPrice

    const availableUserBag = Math.max(0, effectiveUserBag - userBagSpent)

    const calculatedTotalPrice = Math.max(0, advantagePrice + otherPrice)

    return {
        fixedSalary,
        flexibleBag,
        topBag,
        userBag,
        userBagById,
        availableUserBag,
        companyBag,
        pricesById,
        contributionById,
        advantagePrice,
        privateUse,
        renting,
        userBagProrate,
        userBagRestricted,
        totalPrice: calculatedTotalPrice,
        available: Math.min(flexibleBag, available),
        availableTop: topBag ? Math.min(topBag, availableTop) : null,
        activeMonths,
        globalMonthlySpent,
        payPerProduct,
    }
}

export const getCalculation = state => {
    const user = selectProfile(state)
    const children = getFamily(state, 'child')
    const family = getFamily(state)
    const simulatorItems = getSimulatorItems(state)
    const calculator = new Calculator(user, children, family)

    let { withFlex, withoutFlex } = calculator.calculate(simulatorItems)
    const saving = withFlex.netAvailableSalary - withoutFlex.netAvailableSalary

    // get max available
    const { flexibleBag } = user

    withoutFlex = calculator.generate(flexibleBag, 0, {}, 0, true)
    withFlex = calculator.generate(flexibleBag, 0, {}, 0)
    const maxSaving = withFlex.netAvailableSalary - withoutFlex.netAvailableSalary

    return {
        saving,
        pending: maxSaving - saving,
    }
}
